import { FC, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import RocketOutlineIcon from "@src/lib/icons/18px/rocket-outline";
import ArrowUpRightIcon from "@src/lib/icons/18px/arrow-up-right";
import Sparkles2FillIcon from "@src/lib/icons/18px/sparkles-2-fill";
import NewspaperOutlineIcon from "@src/lib/icons/18px/newspaper-outline";
import useNewMessagingEnabled from "@src/features/messaging/posts/useNewMessagingEnabled";
import { UserType } from "@src/types.generated";
import {
  featuresFor,
  ReleaseHeadlineRelease,
  trackedReleaseUrl,
} from "@src/features/home/releaseHeadline/releases";

const STAGGER_S = 0.06;

// Between "Get early access" and the power-up: what turning it on does, and
// what it leaves alone (Newsfeed and Messages keep their own launch date).
const OptInConfirm: FC<{
  release: ReleaseHeadlineRelease;
  excludeSlugs: string[];
  onBack: () => void;
  onConfirm: () => void;
  onFeatureClick: (slug: string) => void;
}> = ({ release, excludeSlugs, onBack, onConfirm, onFeatureClick }) => {
  const onNewsfeed = useNewMessagingEnabled();
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // Focus moves into the step, off the covered letter.
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  const features = featuresFor(release, UserType.Admin).filter(
    (feature) => !excludeSlugs.includes(feature.slug),
  );
  const rise = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: {
            delay: i * STAGGER_S,
            duration: 0.3,
            ease: "easeOut" as const,
          },
        };
  return (
    <div
      ref={ref}
      tabIndex={-1}
      data-testid="release-opt-in-confirm"
      className="absolute inset-0 z-10 flex flex-col bg-bg-primary outline-none"
    >
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-6 overflow-y-auto bg-gradient-to-b from-brand-bg to-bg-primary to-40% px-12 pt-10 pb-8">
        {/* The hero */}
        <motion.div {...rise(0)} className="flex flex-col items-start gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-bg-primary px-3 py-1 text-brand-text shadow-default">
            <Sparkles2FillIcon className="size-3.5" />
            <Text variant="fine-print-title">
              {release.month} release · Early access
            </Text>
          </span>
          <Text variant="display" className="text-text-primary">
            Get the {release.month} release today
          </Text>
          <Text
            variant="body-description"
            multiline
            className="max-w-xl text-text-secondary"
          >
            You can get early access to the {release.month} release today.
            Everyone else gets it on November 11. Here’s what will happen:
          </Text>
        </motion.div>

        {/* What turns on, right away */}
        <div className="flex flex-col gap-3">
          <Text variant="fine-print-title" className="text-text-tertiary">
            Turns on for your company right away
          </Text>
          {/* Two per row: equal bases that grow to share the row. min-w-0 all
              the way down lets the truncated deck shrink instead of widening
              its card to the deck's full length. */}
          <ul className="m-0 flex list-none flex-wrap gap-2.5 p-0">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.li
                  key={feature.slug}
                  {...rise(i + 1)}
                  className="flex min-w-0 flex-1 basis-[40%]"
                >
                  {/* More on the feature, on the release page */}
                  <a
                    href={`${trackedReleaseUrl(release, "nav-letter")}#${feature.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`release-opt-in-confirm-feature-${feature.slug}`}
                    onClick={() => onFeatureClick(feature.slug)}
                    className="group flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-3 shadow-default transition-colors hover:border-brand-border hover:bg-state-hover focus-visible:ring-[3px] focus-visible:ring-brand-border focus-visible:outline-none"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-bg text-brand-text">
                      <Icon />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <Text
                        variant="compact-title"
                        className="text-text-primary"
                      >
                        {feature.title}
                      </Text>
                      <Text
                        variant="fine-print-description"
                        className="truncate text-text-secondary"
                      >
                        {feature.deck}
                      </Text>
                    </span>
                    <ArrowUpRightIcon className="ml-auto size-4 shrink-0 text-icon-tertiary opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </motion.li>
              );
            })}
          </ul>
        </div>

        {/* What stays as it is */}
        <motion.div
          {...rise(features.length + 1)}
          className="flex items-start gap-3 rounded-xl bg-bg-secondary p-4"
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-bg-primary text-icon-secondary">
            <NewspaperOutlineIcon />
          </span>
          <span className="flex flex-col gap-0.5">
            <Text variant="compact-title" className="text-text-primary">
              {onNewsfeed
                ? "Newsfeed and Messages are already on"
                : "Newsfeed and Messages wait for you"}
            </Text>
            <Text
              variant="compact-description"
              multiline
              className="text-text-secondary"
            >
              {onNewsfeed
                ? "Nothing changes there for your team."
                : "You can start setting them up right after this. They stay off for your team until the launch date you pick."}
            </Text>
          </span>
        </motion.div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-secondary px-12 py-4">
        <Button variant="ghost" onClick={onBack}>
          Not yet
        </Button>
        <Button data-testid="release-opt-in-confirm-button" onClick={onConfirm}>
          <RocketOutlineIcon data-icon="inline-start" />
          Turn it on
        </Button>
      </div>
    </div>
  );
};

export default OptInConfirm;
